import React from 'react';
import { Box, Button, Text, VStack } from '@chakra-ui/react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../styles/datepicker-custom.css';
import { Campaign, Collab } from '@foodfluence/shared';
import { getStartDate, getEndDate } from '../utils/dateUtils';
import { getMinPostingDate, getMaxPostingDate, getPassedGoLivePostingDay, passedPostingDayMessage } from 'utils/postingDateValidation';
import { useDueDateTimezone } from 'hooks/useDueDateTimezone';

interface DateSelectorProps {
  selectedDate: Date | null;
  onDateChange: (date: Date) => void;
  campaign?: Campaign;
  collab?: Collab;
  availableDates?: Date[];
  usePostingDateLimits?: boolean; // New prop to enable posting date validation
  /** Go-live posting day passed: the extension request entry point shown instead of the calendar. */
  onRequestExtension?: () => void;
  extensionRequested?: boolean;
}

const DateSelector: React.FC<DateSelectorProps> = ({
  selectedDate,
  onDateChange,
  campaign,
  collab,
  availableDates,
  usePostingDateLimits = false,
  onRequestExtension,
  extensionRequested = false,
}) => {
  const isDateAvailable = (date: Date): boolean => {
    if (!availableDates) return true;
    return availableDates.some(availableDate => 
      availableDate.getFullYear() === date.getFullYear() &&
      availableDate.getMonth() === date.getMonth() &&
      availableDate.getDate() === date.getDate()
    );
  };

  const dueDateTimezone = useDueDateTimezone(collab, campaign);

  // Use posting date limits if enabled, otherwise use campaign start/end dates
  const minDate = usePostingDateLimits && collab 
    ? getMinPostingDate(collab, campaign, dueDateTimezone)
    : getStartDate(campaign);
    
  const maxDate = usePostingDateLimits && collab
    ? getMaxPostingDate(collab, campaign, dueDateTimezone)
    : getEndDate(campaign, collab);

  // Go-live: the posting day is over (min after max), so no day can be picked. Say so instead.
  const passedDay = usePostingDateLimits && collab && campaign ? getPassedGoLivePostingDay(collab, campaign, dueDateTimezone) : null;
  if (passedDay) {
    return (
      <VStack spacing="10px" py={2} align="stretch" fontFamily="display">
        <Text p="12px 14px" borderRadius="14px" bg="#F2F2F0" fontSize="14px" lineHeight="1.4" color="#3E3E42" role="status">
          {passedPostingDayMessage(passedDay)}
        </Text>
        {onRequestExtension && (
          <Button
            h="44px"
            borderRadius="999px"
            border="1.5px solid #0E0E0E"
            bg="#FFFFFF"
            color="#0E0E0E"
            fontSize="14px"
            fontWeight="600"
            _hover={{ bg: '#F2F2F0' }}
            onClick={onRequestExtension}
            isDisabled={extensionRequested}
          >
            {extensionRequested ? 'Extension Requested' : 'Request Extension'}
          </Button>
        )}
      </VStack>
    );
  }

  return (
    <Box display="flex" alignItems="center" justifyContent="center" fontFamily="display">
      <DatePicker
        selected={selectedDate}
        onChange={(date: Date) => {
          onDateChange(date);
        }}
        minDate={minDate}
        maxDate={maxDate}
        filterDate={date => isDateAvailable(date)}
        inline
      />
    </Box>
  );
};

export default DateSelector; 